import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

import { useAdminAuthStore } from '@/store/admin/adminAuthStore';

import { adminSignOut } from '@/utils/auth';

import { DataErrorType } from '../types';
import { axiosConfig } from './const';
import { checkEnvError, logOnDev } from './utils';

checkEnvError();
const adminAuthAxios: AxiosInstance = axios.create(axiosConfig);

adminAuthAxios.interceptors.request.use(onRequest);
adminAuthAxios.interceptors.response.use(onResponse, onErrorResponse);

/**
 * axios request
 */
function onRequest(
  config: AxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  const { method, url } = config;

  logOnDev(`🚀  [API] ${method?.toUpperCase()} ${url} | Request`);

  // 토큰 셋팅
  const accessToken = useAdminAuthStore.getState().adminAccessToken;

  if (accessToken) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return Promise.resolve({ ...config } as InternalAxiosRequestConfig);
}

/**
 * axios response
 */
function onResponse(response: AxiosResponse): AxiosResponse {
  const { method, url } = response.config;
  const { status } = response;

  logOnDev(`🚀 [API] ${method?.toUpperCase()} ${url} | Response ${status}`);

  // TODO: 백엔드 상황별 response 확인필요
  if (status === 204) {
    return response;
  }

  // 응답 실패 처리
  if (!response.data.success) {
    throw response.data;
  }

  return response;
}

/**
 * axios error response
 */
function onErrorResponse(
  error: AxiosError<DataErrorType>,
): Promise<AxiosError> {
  if (axios.isAxiosError(error) && error?.response) {
    const { method, url } = error.config as AxiosRequestConfig;
    const { status } = error.response as AxiosResponse;

    logOnDev(
      `💀 [API] ${method?.toUpperCase()} ${url} | Error ${status} ${
        error.response.data.message
      }`,
    );

    // TODO: status 코드별 처리 필요여부 체크
    switch (status) {
      // 토큰 만료시 권한에러
      // TODO: 추후(다음 프로젝트) 리프레시 토큰 쿠키에 담아서 처리예정
      case 401: {
        adminSignOut();
        break;
      }
      default: {
        break;
      }
    }
  } else if (error instanceof Error && error.name === 'TimeoutError') {
    logOnDev(`💀 [API] | TimeError 요청 시간이 초과되었습니다.`);
  } else if (error?.response) {
    logOnDev(`💀 [API]  | Error ${error.response.data.message}`);
  }

  // TODO: 에러 데이터 처리
  return Promise.reject(error);
}

export default adminAuthAxios;
